import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProgressBarProps } from '@common-fe/common-fe';

import { useTermsAndConditionsDocsQuery } from '@/modules/investments/docs';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';
import { EmployeeAddress, StatementType } from '@/modules/user/user.types';

import useConfirmAddressQuery from '../queries/useConfirmAddress.query';
import useReturnedItemsQuery from '../queries/useReturnedItems.query';
import { useBadAddressReviewStore } from '../stores/useBadAddressReview.store';

import useCanEditAddress from './useCanEditAddress';
import useIsAddressUndeliverable from './useIsAddressUndeliverable';
import useTermsAgreement from './useTermsAgreement';

export enum ReviewOption {
  CORRECT = 'CORRECT',
  NEEDS_UPDATE = 'NEEDS_UPDATE',
}

export enum ReviewStep {
  REVIEW = 'REVIEW',
  EDIT = 'EDIT',
  RETURNED_ITEMS = 'RETURNED_ITEMS',
}

const useReviewAddressModal = (storeKey?: string) => {
  const { t } = useTranslation();
  const { isModalVisible, handleOpenModal, handleCloseModal } = useBadAddressReviewStore(storeKey);
  const { addressId, refetch } = useIsAddressUndeliverable();
  const { canEdit, isLimitedAccess, isAddressEditingAllowed, statementType } = useCanEditAddress();
  const { confirmAddress, isLoading: isConfirming } = useConfirmAddressQuery();
  const { hasQualifyingLetters } = useReturnedItemsQuery();

  const { termsAndConditionsLink: hsaCustodialLink } = useTermsAndConditionsDocsQuery();
  const { employee } = useEmployeeStore();
  const isPaperless = employee?.isElectronicStatementOnly;

  const {
    termsAccepted,
    isAgreementOpened,
    isWarningShaking,
    setTermsAccepted,
    handleOpenAgreement,
    triggerWarningShake,
    resetTermsAgreement,
  } = useTermsAgreement(hsaCustodialLink);

  const canOfferPaperless = useMemo(
    () => !isPaperless && statementType === StatementType.PAPER_AND_ELECTRONIC,
    [isPaperless, statementType]
  );

  const [activeStep, setActiveStep] = useState<ReviewStep>(ReviewStep.REVIEW);
  const [selectedOption, setSelectedOption] = useState<ReviewOption | null>(null);
  const [isGoPaperlessVisible, setGoPaperlessVisible] = useState(false);
  const [isSuccessVisible, setSuccessVisible] = useState(false);

  const currentAddress = useMemo((): EmployeeAddress | undefined => {
    return employee?.addresses?.find((address: EmployeeAddress) => address?.preferredMailing);
  }, [employee]);

  const formattedAddress = useMemo(() => {
    if (!currentAddress) return '';
    const parts = [
      currentAddress.line1,
      currentAddress.line2,
      currentAddress.city,
      currentAddress.state,
      currentAddress.zipcode,
    ].filter(Boolean);
    return parts.join(', ');
  }, [currentAddress]);

  const isEditFlow = useMemo(
    () => selectedOption === ReviewOption.NEEDS_UPDATE && canEdit,
    [selectedOption, canEdit]
  );

  const isReachOutEmployer = useMemo(
    () => selectedOption === ReviewOption.NEEDS_UPDATE && !isAddressEditingAllowed,
    [selectedOption, isAddressEditingAllowed]
  );

  const stepperOptions = useMemo<ProgressBarProps['steps']>(() => {
    const options: ProgressBarProps['steps'] = [{ key: ReviewStep.REVIEW, value: t('Review address') }];
    if (isEditFlow) {
      options.push({ key: ReviewStep.EDIT, value: t('Edit address') });
    }
    options.push({ key: ReviewStep.RETURNED_ITEMS, value: t('Returned Items') });
    return options;
  }, [isEditFlow, t]);

  const handleClose = useCallback(() => {
    handleCloseModal();
    setActiveStep(ReviewStep.REVIEW);
    setSelectedOption(null);
    resetTermsAgreement();
  }, [handleCloseModal, resetTermsAgreement]);

  const confirmIfNeeded = useCallback(async () => {
    if (selectedOption === ReviewOption.CORRECT && addressId) {
      await confirmAddress(addressId);
    }
  }, [selectedOption, addressId, confirmAddress]);

  const handleFinish = useCallback(async () => {
    if (isReachOutEmployer) {
      handleClose();
      return;
    }

    if (canOfferPaperless) {
      handleCloseModal();
      setGoPaperlessVisible(true);
      return;
    }

    await confirmIfNeeded();
    handleCloseModal();
    setActiveStep(ReviewStep.REVIEW);
    setTimeout(() => {
      setSuccessVisible(true);
    }, 300);
  }, [isReachOutEmployer, handleClose, canOfferPaperless, confirmIfNeeded, handleCloseModal]);

  const handleNext = useCallback(() => {
    if (activeStep === ReviewStep.REVIEW) {
      if (isEditFlow) {
        setActiveStep(ReviewStep.EDIT);
      } else {
        setActiveStep(ReviewStep.RETURNED_ITEMS);
      }
      return;
    }
    if (activeStep === ReviewStep.RETURNED_ITEMS) {
      handleFinish();
    }
  }, [activeStep, isEditFlow, handleFinish]);

  const handleEditSuccess = useCallback(() => {
    setActiveStep(ReviewStep.RETURNED_ITEMS);
  }, []);

  const handleBack = useCallback(() => {
    resetTermsAgreement();
    if (activeStep === ReviewStep.RETURNED_ITEMS && isEditFlow) {
      setActiveStep(ReviewStep.EDIT);
      return;
    }
    setActiveStep(ReviewStep.REVIEW);
  }, [activeStep, isEditFlow, resetTermsAgreement]);

  const handleGoPaperlessClose = useCallback(() => {
    setGoPaperlessVisible(false);
  }, []);

  const handleGoPaperlessBack = useCallback(() => {
    setGoPaperlessVisible(false);
    setActiveStep(ReviewStep.RETURNED_ITEMS);
    handleOpenModal();
  }, [handleOpenModal]);

  const handleGoPaperlessSubmit = useCallback(async () => {
    await confirmIfNeeded();
    setGoPaperlessVisible(false);
    setActiveStep(ReviewStep.REVIEW);
    setTimeout(() => {
      setSuccessVisible(true);
    }, 300);
  }, [confirmIfNeeded]);

  const handleSuccessClose = useCallback(() => {
    setSuccessVisible(false);
    setSelectedOption(null);
    resetTermsAgreement();
    refetch();
  }, [refetch, resetTermsAgreement]);

  const isTermsStepBlocked = useMemo(
    () => activeStep === ReviewStep.RETURNED_ITEMS
      && !isReachOutEmployer
      && hasQualifyingLetters
      && !termsAccepted,
    [activeStep, isReachOutEmployer, hasQualifyingLetters, termsAccepted]
  );

  const isNextDisabled = useMemo(() => {
    if (activeStep === ReviewStep.REVIEW) {
      return !selectedOption;
    }
    if (activeStep === ReviewStep.RETURNED_ITEMS) {
      return isConfirming || isTermsStepBlocked;
    }
    return false;
  }, [activeStep, selectedOption, isConfirming, isTermsStepBlocked]);

  const nextButtonLabel = useMemo(() => {
    if (isReachOutEmployer && activeStep === ReviewStep.RETURNED_ITEMS) {
      return t('Close');
    }
    return t('Next');
  }, [isReachOutEmployer, activeStep, t]);

  const stepTitle = useMemo(() => {
    if (activeStep === ReviewStep.EDIT) {
      return t('Edit Address');
    }
    if (activeStep === ReviewStep.RETURNED_ITEMS) {
      return t('Returned Items');
    }
    return t('Review Address');
  }, [activeStep, t]);

  const successHelpText = useMemo(() => {
    // Case 7: no subtext under "You're all set!" for now.
    // if (isEditFlow) {
    //   return t('Your address has been successfully updated!');
    // }
    // return t('You can view your statements within the documents section of your account.');
    return undefined;
  }, []);

  return {
    isModalVisible,
    activeStep,
    stepperOptions,
    selectedOption,
    currentAddress,
    formattedAddress,
    isLimitedAccess,
    isEditFlow,
    isReachOutEmployer,
    hasQualifyingLetters,
    termsAccepted,
    hsaCustodialLink,
    isAgreementOpened,
    isWarningShaking,
    isGoPaperlessVisible,
    isSuccessVisible,
    isNextDisabled,
    nextButtonLabel,
    stepTitle,
    successHelpText,
    setSelectedOption,
    setTermsAccepted,
    handleOpenAgreement,
    triggerWarningShake,
    handleNext,
    handleBack,
    handleClose,
    handleEditSuccess,
    handleGoPaperlessClose,
    handleGoPaperlessBack,
    handleGoPaperlessSubmit,
    handleSuccessClose,
  };
};

export default useReviewAddressModal;
